import { useEffect, useRef, useState } from 'react';
import { useScraperOutputSnapshot } from '../../../api/queries';
import { useScraperOutput } from '../../../api/socket';
import { clock } from '../../../lib/time';

const MAX_LINES = 2000;

/** The design synthesised {level, time, message} triples. The real endpoint
 *  returns a flat stdout blob, so lines are rendered verbatim and levels are
 *  only *inferred* from the text — never invented. */
function levelOf(line: string): '' | 'warn' | 'err' {
  if (/\b(ERROR|CRITICAL|Traceback|Exception)\b/i.test(line)) return 'err';
  if (/\b(WARN|WARNING)\b/i.test(line)) return 'warn';
  return '';
}

export function LogsTab({ name }: { name: string }) {
  const { data, isLoading } = useScraperOutputSnapshot(name);
  const [live, setLive] = useState<string[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);

  // Reset when switching scrapers so one scraper's output can't bleed into
  // another's pane.
  useEffect(() => setLive([]), [name]);

  useScraperOutput(name, (output) => {
    setLive((prev) => [...prev, ...output.split('\n')].slice(-MAX_LINES));
  });

  const seeded = (data?.output ?? '').split('\n').filter(Boolean);
  const lines = [...seeded, ...live].slice(-MAX_LINES);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    // Only autoscroll if already near the bottom, so reading scrollback isn't
    // yanked away by an incoming line.
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 60;
    if (nearBottom) box.scrollTop = box.scrollHeight;
  }, [lines.length]);

  return (
    <div className="pcard">
      <div className="head">
        <h3>Run output</h3>
        <span className="spacer" />
        <span className="mono-sm muted">
          {data?.last_run ? `last run ${clock(data.last_run)}` : ''}
          {lines.length >= MAX_LINES ? ` · trimmed to ${MAX_LINES} lines` : ''}
        </span>
      </div>
      <div className="logbox" ref={boxRef}>
        {isLoading && <div className="logline">loading…</div>}
        {!isLoading && lines.length === 0 && (
          <div className="logline">
            <span className="lm muted">No output captured yet.</span>
          </div>
        )}
        {lines.map((line, i) => {
          const level = levelOf(line);
          return (
            <div key={i} className={`logline ${level}`}>
              <span className="lm">{line}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
